"use client";

import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';

export type AddressManagerOptions = {
  anonymousMode?: boolean;
  disableClose?: boolean;
  isFirstVisitFlow?: boolean;
  forceOpen?: boolean; // NUEVO: Permite abrir el manager aunque ya haya una ubicación (ej. desde el menú de usuario)
};

type AddressManagerContextType = {
  isOpen: boolean;
  options: AddressManagerOptions;
  toggle: () => void;
  open: (options?: AddressManagerOptions) => void;
  close: () => void;
};

export const AddressManagerContext = createContext<AddressManagerContextType | undefined>(undefined);

export const AddressManagerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<AddressManagerOptions>({});

  const toggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const open = useCallback((newOptions?: AddressManagerOptions) => {
    setOptions(newOptions || {});
    setIsOpen(true);
    window.dispatchEvent(new CustomEvent('sidebar-opened', { detail: { type: 'address-manager' } }));
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setOptions({});
  }, []);

  // Listen for other sidebars opening
  React.useEffect(() => {
    const handleSidebarOpened = (event: CustomEvent) => {
      if (event.detail.type !== 'address-manager') {
        setIsOpen(false);
      }
    };

    window.addEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    return () => {
      window.removeEventListener('sidebar-opened', handleSidebarOpened as EventListener);
    };
  }, []);

  return (
    <AddressManagerContext.Provider value={{ isOpen, options, toggle, open, close }}>
      {children}
    </AddressManagerContext.Provider>
  );
};

export const useAddressManager = () => {
  const context = useContext(AddressManagerContext);
  if (context === undefined) {
    throw new Error('useAddressManager must be used within an AddressManagerProvider');
  }
  return context;
};
